import * as React from "react";
declare const I18n;

import { Lesson } from "../../../../types";

import Header from "../../../Header";
import BreadCrumb from "../../../BreadCrumb";
import ShowInteractiveLesson from "../show/show";
import WomenInStemModal from "../../../Achievements/modals/WomenInStemModal"

import api from "../../../../api";


type Props = {
  initialData: {
    courseId: number;
    courseTitle?: string;
    lessons: Lesson[];
    userType?: string;
    lessonsProgress: any
    activeLessonId: any
    lessonSubmissions: any;
    facilitatorCourseProgress?: any;
    quizQuestionsByLesson: any;
    courseLevel?: number;
    miniLesson?: boolean;
    learnersCourseCompleted: number;
    totalLearners: number;
  };
};
const IndexLesson: React.FunctionComponent<Props> = ({
  initialData: { courseId, courseTitle, lessons, userType, lessonsProgress, activeLessonId, lessonSubmissions, facilitatorCourseProgress, quizQuestionsByLesson, courseLevel, miniLesson, learnersCourseCompleted, totalLearners },
}: Props): JSX.Element => {
  const isAdmin = !(
    userType &&
    (userType === "Learner" || userType === "Facilitator")
  );
  const isFacilitator = userType && userType === "Facilitator";
  const [allLessons, setAllLessons] = React.useState(lessons);
  const [currentLesson, setCurrentLesson] = React.useState(lessons[0]);
  const [currentLessonCompleted, setCurrentLessonCompleted] = React.useState(false);
  const [sectionIndex, setSectionIndex] = React.useState(0);
  const [showAllSections, setShowAllSections] = React.useState(!miniLesson);
  const [lockedNumber, setLockedNumber] = React.useState(0);

  const calculateTotalDuration = (lessons: Lesson[]) => {
    return lessons.reduce((total, lesson) => {
      return total + parseFloat(lesson.duration || "0");
    }, 0);
  };

  const totalLessons = lessons.length;
  const totalDuration = calculateTotalDuration(lessons);
  const completedLessons = lessons.filter(lesson => lesson.completed).length;

  React.useEffect(() => {
    if (!activeLessonId) return;

    const match = lessons.find((l) => String(l.id) === activeLessonId);
    if (match) {
      setCurrentLesson(match);
    }
  }, [activeLessonId, lessons]);

  React.useEffect(() => {
    const url = new URL(window.location.href);

    if (url.searchParams.has("active_lesson_id")) {
      url.searchParams.delete("active_lesson_id");
      window.history.replaceState({}, "", url.pathname);
    }
  }, []);

  React.useEffect(() => {
    if (userType !== "Learner") return;
    if (!currentLesson) return;

    const isCompleted = lessonSubmissions.some(
      (lesson) => lesson.submittable_id === currentLesson.id
    );

    setCurrentLessonCompleted(isCompleted);
  }, [currentLesson, lessonSubmissions]);

  React.useEffect(() => {
    const lockedNumber = lessons.findIndex((lesson) => !lesson.completed);
    setLockedNumber(lockedNumber === -1 ? lessons.length : lockedNumber);
  }, []);

  React.useEffect(() => {
    if (miniLesson) {
      setShowAllSections(false);
    } else {
      const isFirstLesson = currentLesson?.id === lessons[0]?.id;
      setShowAllSections(isFirstLesson);
    }
  }, [currentLesson, lessons, miniLesson]);

  React.useEffect(() => {
    // If the URL explicitly sets the lesson, don't override it
    if (activeLessonId) {
      const match = lessons.find((l) => String(l.id) === String(activeLessonId));
      if (match) {
        setCurrentLesson(match);
        setSectionIndex(0); // default; your ShowInteractiveLesson can override if it reads URL
      }
      return;
    }

    // Facilitators: load last active lesson + section
    if (!isFacilitator) return;
    if (!lessons || lessons.length === 0) return;

    const parseTs = (row: any) => Date.parse(row?.updated_at || row?.created_at || 0);

    // Pick latest facilitator lesson (by timestamp)
    const latestFacRow =
      Array.isArray(facilitatorCourseProgress)
        ? facilitatorCourseProgress
          .filter((r) => String(r.course_id) === String(courseId))
          .sort((a, b) => parseTs(b) - parseTs(a))[0]
        : null;

    const fallbackLessonId =
      Array.isArray(lessonsProgress)
        ? lessonsProgress
          .filter((r) => String(r.course_id) === String(courseId))
          .sort((a, b) => parseTs(b) - parseTs(a))[0]?.lesson_id
        : null;

    const lessonIdToOpen = latestFacRow?.lesson_id || fallbackLessonId;
    if (!lessonIdToOpen) return;

    const lessonMatch = lessons.find((l) => String(l.id) === String(lessonIdToOpen));
    if (!lessonMatch) return;

    // Pick latest section within that lesson (from lessonsProgress)
    const latestSectionRow =
      Array.isArray(lessonsProgress)
        ? lessonsProgress
          .filter(
            (r) =>
              String(r.course_id) === String(courseId) &&
              String(r.lesson_id) === String(lessonIdToOpen)
          )
          .sort((a, b) => parseTs(b) - parseTs(a))[0]
        : null;

    const sectionId = latestSectionRow?.section_id; // "overview" or "166" etc.

    // Convert section_id -> sectionIndex expected by your UI:
    // 0 => Overview
    // 1..n => lesson.sections index + 1
    const computeSectionIndex = (): number => {
      if (!sectionId) return 0;
      if (String(sectionId) === "overview" || String(sectionId) === "0") return 0;

      // Find matching section by ID inside the lesson
      const sections = lessonMatch.sections || [];
      const idx = sections.findIndex((s: any) => String(s.id) === String(sectionId));
      return idx >= 0 ? idx + 1 : 0;
    };

    setCurrentLesson(lessonMatch);
    setSectionIndex(computeSectionIndex());
  }, [
    activeLessonId,
    isFacilitator,
    facilitatorCourseProgress,
    lessonsProgress,
    lessons,
    courseId
  ]);

  const handleDelete = (id) => {
    if (confirm(I18n.t("views.forms.messaging.confirm_delete"))) {
      api
        .deleteLesson(courseId, id)
        .then((res) => (window.location.href = res.url))
        .catch((error) => console.error("Error"));
    }
  };

  const getIcon = (index) => {
    if (index < lockedNumber) {
      return "fa-check-circle";
    } else if (index === lockedNumber) {
      return "fa-play-circle";
    } else {
      return "fa-lock";
    }
  };

  const getIconColor = (index) => {
    if (index < lockedNumber) {
      return "#5DBA19";
    } else if (index === lockedNumber) {
      return "#813091";
    } else {
      return "#505050";
    }
  };

  const handleNextLesson = () => {
    const currentIndex = allLessons.findIndex(l => l.id === currentLesson.id);

    // Update the completed status of the current lesson
    const updatedLessons = [...allLessons];
    updatedLessons[currentIndex] = {
      ...updatedLessons[currentIndex],
      completed: true
    };

    setAllLessons(updatedLessons);

    // Update lockedNumber to allow access to the next lesson
    const newLockedNumber = updatedLessons.findIndex((lesson) => !lesson.completed);
    setLockedNumber(newLockedNumber === -1 ? updatedLessons.length : newLockedNumber);

    const next = updatedLessons[currentIndex + 1];

    if (next) {
      setCurrentLesson(next);
      setSectionIndex(0);
    } else {
      window.location.href = `/courses`;
    }
  };

  const handlePrevLesson = () => {
    const currentIndex = allLessons.findIndex(l => l.id === currentLesson.id);
    const prev = allLessons[currentIndex - 1];

    if (prev) {
      setCurrentLesson(prev);
      setSectionIndex(0);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  const handleLessonClick = (selectedLesson: Lesson, selectedSectionIndex: number) => {
    // Check if lesson is locked
    const lessonIndex = lessons.findIndex(l => l.id === selectedLesson.id);

    // For learners, prevent clicking on locked lessons
    if (userType === "Learner" && lessonIndex > lockedNumber) {
      return;
    }

    // Update the current lesson and section
    setCurrentLesson(selectedLesson);
    setSectionIndex(selectedSectionIndex);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isAdmin) {
    return (
      <div className="lesson_index">
        <Header
          title={courseTitle}
          titleButton={I18n.t("models.lesson.messaging.create")}
          locationButton={`${window.location.pathname}/new`}
          secondTitleButton="New Quiz"
          secondLocationButton={`${window.location.pathname}/new?lesson_type=quiz`}
          thirdTitleButton="New Feedback Form"
          thirdLocationButton={`${window.location.pathname}/new?lesson_type=feedback_form`}
          buttonDisabled={!isAdmin}
        />

        <BreadCrumb
          locations={[
            {
              title: I18n.t("models.course.attributes.courses"),
              url: `${isAdmin ? "/admin" : ""}/courses`,
            },
            { title: courseTitle, url: "" },
          ]}
        />

        {
          lessons.length > 0 ? (
            lessons.map((lesson, index) => (
              <div className={`lesson_index__row ${userType && userType === 'Learner' && 'lesson_index__row--non-admin'}`} key={lesson.id}>
                {
                  userType && (userType === 'Learner') && (
                    <i
                      className={`fal ${getIcon(index)} mr-4`}
                      style={{ color: getIconColor(index), fontSize: '32px', marginRight: '10px' }}
                    />
                  )
                }

                {index > lockedNumber && userType && userType === "Learner" ? (
                  <div
                    className="lesson_index__title app-text"
                    id={`lesson_id_${lesson.id}`}
                  >
                    {lesson.title}
                  </div>
                ) : (
                  <a
                    className="lesson_index__title app-text"
                    style={{ cursor: "pointer" }}
                    id={`lesson_id_${lesson.id}`}
                    href={`
                      ${isAdmin ? "/admin" : ""}/courses/${courseId}/lessons/${lesson.id
                      }${isAdmin ? "/edit" : ""}
                    `}
                  >
                    {lesson.lesson_type === "quiz" ? "Quiz: " : ""}
                    {lesson.title}
                  </a>
                )}
                <div className="lesson_index__icon-container">
                  {(!userType || userType !== "Learner") &&
                    lesson.lesson_type === "quiz" && (
                      <a
                        className="mr-3"
                        href={`/admin/courses/${courseId}/lessons/${lesson.id}/stats`}
                      >
                        View Stats
                      </a>
                    )}
                  {isAdmin && (
                    <>
                      <a
                        href={`/admin/courses/${courseId}/lessons/${lesson.id}/edit`}
                      >
                        <i className="fal fa-file-search mr-3 lesson_index__icon" />
                      </a>
                      <div
                        id={`delete_${lesson.id}`}
                        onClick={() => handleDelete(lesson.id)}
                      >
                        <i
                          className="fal fa-trash lesson_index__icon"
                          aria-label="Delete lesson"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="lesson_index__empty-state app-text">
              {I18n.t("models.lesson.messaging.empty_state")}
            </div>
          )}
      </div>
    );
  }

  return (
    <>
      <ShowInteractiveLesson
        initialData={{
          lesson: currentLesson,
          courseId,
          courseLevel: 0,
          isFacilitator: isFacilitator,
          lessonCompleted: currentLessonCompleted,
          renderFromLessonIndex: true,
          allLessons: allLessons,
          courseLevel: courseLevel,
          miniLesson: miniLesson,
          lessonsProgress: lessonsProgress,
          facilitatorCourseProgress: facilitatorCourseProgress,
        }}
        sectionIndex={sectionIndex}
        onNextLesson={handleNextLesson}
        onPreviousLesson={handlePrevLesson}
        onLessonClick={handleLessonClick}
        quizQuestionsByLesson={quizQuestionsByLesson[currentLesson.id]}
        showAllSections={showAllSections}
        totalLessons={totalLessons}
        totalDuration={totalDuration}
        completedLessons={completedLessons}
        learnersCourseCompleted={learnersCourseCompleted}
        totalLearners={totalLearners}
      />
    </>
  );
};

export default IndexLesson;